import { FontAwesome5, MaterialIcons, Ionicons } from "@expo/vector-icons";
import React, { useState, useEffect, useRef } from "react";
import { Platform, ScrollView, Text, View, TouchableOpacity, ActivityIndicator, Animated } from "react-native";
import Fade from "react-native-fade";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useQuery } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  Centered,
  Input,
  Loading,
  Row,
  Modal,
  ModalButtons,
  StepHeader,
} from "../../../components/all";
import { addFields } from "../../../reducers/auth/register";
import { getProfessionsByType } from "../../../services/shared";
import { getLocalizedProfessionName, getLocalizedProfessionTypeName, extractProfessionTitleAndSubtitle, sortProfessionsGeneralFirst } from "../../../utils/professionUtils";
import { normalize } from "../../../utils/normalize";
import Styles from "../../../constants/styles";
import colors from "../../../constants/colors";

export default function StepCategory({ editProfile, navigation, route }) {
  const { country: userCountry } = useSelector((state) => state.user);
  const registerCountry = useSelector((state) => state.auth?.register?.country);
  const country = registerCountry || userCountry || 'MY';

  const dispatch = useDispatch();
  const { fields } = useSelector((state) => state.auth?.register || {});
  const { isDoctor } = useSelector((state) => state.user);
  const safeInsets = useSafeAreaInsets();

  // Get profession type from previous screen
  const professionTypeCode = route.params?.profession_type_code || fields?.profession_type_code;
  const professionType = route.params?.profession_type || fields?.profession_type;
  const jobType = route.params?.jobType;
  const isPostJob = route.name === "PostJobCategoryScreen";

  const [selectedProfessions, setSelectedProfessions] = useState(fields?.professions || []);
  const [showGuideModal, setShowGuideModal] = useState(true);
  const [filterInput, setFilterInput] = useState("");
  const [showError, setShowError] = useState(false);
  const [postJobData, setPostJobData] = useState({
    professions: [],
    profession_type_code: professionTypeCode,
  });
  const animatedValues = useRef({});
  const [animationsInitialized, setAnimationsInitialized] = useState(false);

  // Fetch professions filtered by type
  const { data: professionsData, isLoading, error, refetch } = useQuery(
    ['professions', country, professionTypeCode],
    () => getProfessionsByType(country, professionTypeCode),
    {
      enabled: !!professionTypeCode,
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
      onError: (error) => {
        console.error('Failed to fetch professions:', error);
      }
    }
  );

  const professions = professionsData?.data?.data || [];

  // Filter professions based on search and sort alphabetically, with the
  // list's "general" entry (e.g. General Practitioner) pinned to the top
  const filteredProfessions = sortProfessionsGeneralFirst(
    professions
      .filter(profession => {
        if (!filterInput) return true;
        const name = getLocalizedProfessionName(profession, country, jobType);
        return name.toLowerCase().includes(filterInput.toLowerCase());
      })
      .sort((a, b) => {
        const nameA = getLocalizedProfessionName(a, country, jobType);
        const nameB = getLocalizedProfessionName(b, country, jobType);
        return nameA.localeCompare(nameB);
      }),
    undefined,
    country,
    jobType
  );

  // Initialize animations for each profession
  useEffect(() => {
    if (!isLoading && filteredProfessions.length > 0 && !animationsInitialized) {
      filteredProfessions.forEach((profession, index) => {
        if (!animatedValues.current[profession.id]) {
          animatedValues.current[profession.id] = new Animated.Value(0);
        }
      });

      // Animate entrance
      const animations = filteredProfessions.map((profession, index) => {
        return Animated.timing(animatedValues.current[profession.id], {
          toValue: 1,
          duration: 400,
          delay: index * 50, // Stagger effect
          useNativeDriver: true,
        });
      });

      Animated.parallel(animations).start();
      setAnimationsInitialized(true);
    }
  }, [isLoading, filteredProfessions, animationsInitialized]);

  const toggleProfession = (profession) => {
    setShowError(false);

    if (isPostJob) {
      // For job posting, allow only single selection
      const isSelected = selectedProfessions.some(p => p.id === profession.id);
      if (isSelected) {
        setSelectedProfessions([]);
        setPostJobData({
          ...postJobData,
          professions: [],
        });
      } else {
        setSelectedProfessions([profession]);
        setPostJobData({
          ...postJobData,
          professions: [profession],
        });
      }
    } else {
      // For registration, allow multiple selection
      const isSelected = selectedProfessions.some(p => p.id === profession.id);
      if (isSelected) {
        setSelectedProfessions(selectedProfessions.filter(p => p.id !== profession.id));
      } else {
        setSelectedProfessions([...selectedProfessions, profession]);
      }
    }
  };

  const handleNext = () => {
    if (selectedProfessions.length === 0) {
      setShowError(true);
      return;
    }

    if (isPostJob) {
      // Navigate to PostJobMainScreen with selected professions
      navigation.navigate("PostJobMainScreen", {
        professions: selectedProfessions,
        profession_type_code: professionTypeCode,
        jobType: jobType,
      });
    } else {
      // Save to registration and navigate to next step
      dispatch(addFields({
        ...fields,
        professions: selectedProfessions,
        profession_ids: selectedProfessions.map(p => parseInt(p.id)),
        // Use the first selected profession as the main one for backward compatibility
        profession_id: selectedProfessions[0]?.id,
        expertise: selectedProfessions[0]?.id, // Backward compatibility
      }));
      navigation.navigate("RegisterUserScreen", {
        jobType: jobType,
      });
    }
  };

  // Show loading state
  if (isLoading) {
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
        <ActivityIndicator size="large" color={colors.primary} />
        <Text style={{ marginTop: 16 }}>Loading professions...</Text>
      </View>
    );
  }

  // Show error state
  if (error || !professionTypeCode) {
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', padding: 32 }}>
        <Text style={{ fontSize: 16, textAlign: 'center', marginBottom: 16 }}>
          {!professionTypeCode
            ? "No profession type selected. Please go back and select a type."
            : "Unable to load professions. Please check your internet connection and try again."}
        </Text>
        <Button
          onPress={() => professionTypeCode ? refetch() : navigation.goBack()}
          label={professionTypeCode ? "Retry" : "Go Back"}
          type="primary"
        />
      </View>
    );
  }
  const { title: professionTitle, subtitle: professionSubtitle } = extractProfessionTitleAndSubtitle(getLocalizedProfessionTypeName(professionType, country, jobType));

  return (
    <>
      <ScrollView
        style={{
          ...Styles.webSmallContainer,
          marginTop: 120,
        }}
      >
        <StepHeader
          label={isDoctor ? `Medical\nSpecialization` : `Select\nProfession`}
          icon={
            <FontAwesome5 name="briefcase-medical" size={24} color="black" />
          }
          subtitle={
            isDoctor
              ? `You will receive alerts on jobs of the selected profession.`
              : isPostJob
                ? `Choose a profession you are looking to hire for.`
                : `Select your professional expertise.`
          }
        />

        {/* Show selected profession type */}
        {professionType && (
          <>
            <Text style={{
              fontSize: normalize(18),
              fontWeight: "600",
              color: colors.primary,
              marginTop: 20,
              marginLeft: 24,
            }}>
              {professionTitle}
            </Text>
            <Text style={{
              fontSize: normalize(14),
              color: "rgba(0,0,0,0.5)",
              marginTop: 4,
              marginLeft: 24,
              textTransform: "capitalize",
            }}>
              {professionSubtitle}
            </Text>
          </>
        )}

        <View style={{ padding: 20 }}>
          <Input
            onChangeText={(text) => setFilterInput(text)}
            value={filterInput}
            placeholder={"Search Professions"}
          />

          <Centered style={{ width: "100%" }}>
            <Fade visible={showError} duration={300}>
              <Text
                style={{ fontSize: normalize(10), color: "red", marginTop: 10 }}
              >
                Please select at least one profession
              </Text>
            </Fade>
          </Centered>

          {/* Professions List */}
          <View style={{
            marginTop: 0,
          }}>
            {filteredProfessions.map((profession, index) => {
              const isSelected = selectedProfessions.some(p => p.id === profession.id);
              const animValue = animatedValues.current[profession.id] || new Animated.Value(1);
              const professionName = getLocalizedProfessionName(profession, country, jobType);
              const { title, subtitle } = extractProfessionTitleAndSubtitle(professionName);
              if (!title || title === "#N/A") return null;
              // Get icon and color based on profession code pattern
              const getIconConfig = () => {
                const code = profession.code || '';
                if (code.includes('doc') || code.includes('physician')) {
                  return { icon: 'medical-services', color: '#4B9BFF', bgColor: '#E3F2FF' };
                } else if (code.includes('surg')) {
                  return { icon: 'healing', color: '#10B981', bgColor: '#D1FAE5' };
                } else if (code.includes('pedi')) {
                  return { icon: 'child-care', color: '#F59E0B', bgColor: '#FEF3C7' };
                } else if (code.includes('card')) {
                  return { icon: 'favorite', color: '#EF4444', bgColor: '#FEE2E2' };
                } else if (code.includes('neuro')) {
                  return { icon: 'psychology', color: '#8B5CF6', bgColor: '#EDE9FE' };
                } else {
                  return { icon: 'local-hospital', color: colors.primary, bgColor: colors.primary + '10' };
                }
              };

              const iconConfig = getIconConfig();

              return (
                <Animated.View
                  key={profession.id}
                  style={{
                    opacity: animValue,
                    transform: [
                      {
                        translateY: animValue.interpolate({
                          inputRange: [0, 1],
                          outputRange: [20, 0],
                        }),
                      },
                      {
                        scale: isSelected ? 0.98 : 1,
                      },
                    ],
                  }}
                >
                  <TouchableOpacity
                    onPress={() => toggleProfession(profession)}
                    activeOpacity={0.7}
                    style={{
                      marginBottom: 12,
                      padding: 18,
                      borderRadius: 20,
                      borderWidth: 2,
                      borderColor: isSelected ? iconConfig.color : "#F3F4F6",
                      backgroundColor: "white",
                      flexDirection: 'row',
                      alignItems: 'center',
                      shadowColor: "#000",
                      shadowOffset: {
                        width: 0,
                        height: 4,
                      },
                      shadowOpacity: isSelected ? 0.15 : 0.08,
                      shadowRadius: 12,
                      elevation: 3,
                    }}
                  >
                    {/* Checkbox */}
                    <View
                      style={{
                        width: 20,
                        height: 20,
                        borderRadius: 6,
                        borderWidth: 2,
                        borderColor: isSelected ? iconConfig.color : "#D1D5DB",
                        backgroundColor: isSelected ? iconConfig.color : "white",
                        marginRight: 12,
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      {isSelected && (
                        <MaterialIcons
                          name="check"
                          size={14}
                          color="white"
                        />
                      )}
                    </View>

                    <View style={{ flex: 1 }}>
                      <Text style={{
                        fontSize: normalize(14),
                        color: isSelected ? iconConfig.color : "#1F2937",
                        fontWeight: isSelected ? "600" : "500",
                        letterSpacing: -0.3,
                      }}>
                        {title}
                      </Text>
                      {subtitle && (
                        <Text style={{
                          fontSize: normalize(11),
                          color: isSelected ? iconConfig.color + '99' : "rgba(0, 0, 0, 0.5)",
                          fontWeight: "400",
                          marginTop: 2,
                          letterSpacing: -0.2,
                        }}>
                          {subtitle}
                        </Text>
                      )}
                    </View>

                  </TouchableOpacity>
                </Animated.View>
              );
            })}
          </View>

          {filteredProfessions.length === 0 && (
            <Centered style={{ marginTop: 40 }}>
              <Text style={{
                fontSize: normalize(14),
                color: "rgba(0,0,0,0.5)",
                textAlign: 'center',
              }}>
                No professions found matching "{filterInput}"
              </Text>
            </Centered>
          )}
        </View>

        <View style={{ marginBottom: 140 }} />
      </ScrollView>

      <View
        style={{
          paddingBottom: safeInsets.bottom,
          paddingTop: 10,
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: "white",
          shadowColor: "#000",
          shadowOffset: { width: 0, height: -2 },
          shadowOpacity: 0.1,
          shadowRadius: 3,
          elevation: 5,
        }}
      >
        <Centered>
          <Row style={{ marginBottom: 24 }}>
            <Button
              label="Back"
              type={"secondary"}
              onPress={() => {
                navigation.goBack();
              }}
            />
            <Button
              label="Next"
              type={"primary"}
              onPress={handleNext}
            />
          </Row>
        </Centered>
      </View>
      {isDoctor && (
        <Modal
          isVisible={showGuideModal}
          onBackdropPress={() => setShowGuideModal(false)}
        >
          <View style={{ alignItems: "center", marginBottom: 12 }}>
            <Ionicons name="checkmark-circle-outline" size={24} color="#FF4B4B" />
          </View>
          <Text
            style={{
              fontWeight: "bold",
              fontSize: normalize(16),
              textAlign: "center",
            }}
          >
            Multiple Professions
          </Text>
          <Text
            style={{
              textAlign: "center",
              marginTop: 10,
              marginBottom: 24,
              marginHorizontal: 24,
            }}
          >
            You may select more than one profession to receive alerts on.
          </Text>
          <ModalButtons
            onCancel={() => setShowGuideModal(false)}
            onConfirm={() => {
              setShowGuideModal(false);
            }}
          />
        </Modal>
      )}
      {Platform.OS === "web" ? null : (
        <Loading visible={isLoading} />
      )}
    </>
  );
}